import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";
import { useTour, TOUR_STEPS } from "../../contexts/TourContext";

interface TooltipPosition {
  top: number;
  left: number;
  arrowSide: "left" | "right" | "top" | "bottom";
}

const TOOLTIP_WIDTH = 300;
const TOOLTIP_OFFSET = 16;

function getTooltipPosition(
  rect: DOMRect,
  placement: string,
  vpW: number,
  vpH: number,
): TooltipPosition {
  let top = 0;
  let left = 0;
  let arrowSide: TooltipPosition["arrowSide"] = "left";

  switch (placement) {
    case "right":
      top = rect.top + rect.height / 2 - 60;
      left = rect.right + TOOLTIP_OFFSET;
      arrowSide = "left";
      // If it overflows the right edge, flip to left
      if (left + TOOLTIP_WIDTH > vpW - 8) {
        left = rect.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
        arrowSide = "right";
      }
      break;
    case "left":
      top = rect.top + rect.height / 2 - 60;
      left = rect.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
      arrowSide = "right";
      if (left < 8) {
        left = rect.right + TOOLTIP_OFFSET;
        arrowSide = "left";
      }
      break;
    case "bottom":
      top = rect.bottom + TOOLTIP_OFFSET;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      arrowSide = "top";
      break;
    case "top":
    default:
      top = rect.top - TOOLTIP_OFFSET - 130;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      arrowSide = "bottom";
      break;
  }

  // Clamp to viewport
  top = Math.max(8, Math.min(top, vpH - 150));
  left = Math.max(8, Math.min(left, vpW - TOOLTIP_WIDTH - 8));

  return { top, left, arrowSide };
}

const ARROW_CLASSES: Record<TooltipPosition["arrowSide"], string> = {
  left: "top-1/2 -translate-y-1/2 -left-2 border-r-white border-y-transparent border-l-transparent border-8",
  right:
    "top-1/2 -translate-y-1/2 -right-2 border-l-white border-y-transparent border-r-transparent border-8",
  top: "left-1/2 -translate-x-1/2 -top-2 border-b-white border-x-transparent border-t-transparent border-8",
  bottom:
    "left-1/2 -translate-x-1/2 -bottom-2 border-t-white border-x-transparent border-b-transparent border-8",
};

const ProductTour: React.FC = () => {
  const { isActive, currentStep, totalSteps, step, next, skip } = useTour();
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive || !step) {
      setTooltipPos(null);
      setHighlightRect(null);
      return;
    }

    const update = () => {
      const el = document.getElementById(step.targetId);
      if (!el) {
        setTooltipPos(null);
        setHighlightRect(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setHighlightRect(rect);
      const pos = getTooltipPosition(
        rect,
        step.placement ?? "right",
        window.innerWidth,
        window.innerHeight,
      );
      setTooltipPos(pos);
    };

    update();

    // Re-calculate on resize
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isActive, step]);

  if (!isActive || !step || !tooltipPos || !highlightRect) return null;

  const PADDING = 6;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] pointer-events-none">
        {/* Dark overlay with cut-out using SVG */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-auto"
          onClick={skip}
          style={{ cursor: "default" }}
        >
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={highlightRect.left - PADDING}
                y={highlightRect.top - PADDING}
                width={highlightRect.width + PADDING * 2}
                height={highlightRect.height + PADDING * 2}
                rx={8}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.55)"
            mask="url(#tour-mask)"
          />
        </svg>

        {/* Highlight ring */}
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: highlightRect.top - PADDING,
            left: highlightRect.left - PADDING,
            width: highlightRect.width + PADDING * 2,
            height: highlightRect.height + PADDING * 2,
            boxShadow: "0 0 0 3px rgba(255,255,255,0.6)",
          }}
        />

        {/* Tooltip */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.2 }}
          className="absolute pointer-events-auto"
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            width: TOOLTIP_WIDTH,
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-5 relative">
            {/* Arrow */}
            <div
              className={`absolute border-solid ${ARROW_CLASSES[tooltipPos.arrowSide]}`}
            />

            {/* Header */}
            <div className="flex items-start justify-between mb-2 gap-2">
              <h3 className="text-[#1d365d] font-semibold text-base leading-snug">
                {step.title}
              </h3>
              <button
                onClick={skip}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5 transition-colors"
                aria-label="Close tour"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              {step.description}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between">
              {/* Step dots */}
              <div className="flex gap-1.5">
                {TOUR_STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-200 ${
                      i === currentStep
                        ? "w-4 bg-[#1d365d]"
                        : i < currentStep
                          ? "w-1.5 bg-[#1d365d]/40"
                          : "w-1.5 bg-gray-200"
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={skip}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Skip tour
                </button>
                <button
                  onClick={next}
                  className="flex items-center gap-1 bg-[#1d365d] hover:bg-[#162b4a] text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
                >
                  {currentStep < totalSteps - 1 ? (
                    <>
                      Next <ChevronRight size={14} />
                    </>
                  ) : (
                    "Done"
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProductTour;
